import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TableModule } from '@openng/optimus-ui/table';
import { Recipient } from '../../../../models/email-template/recipient';
import { ResponsysService } from '../../../../services/responsys.service';
import { ToastBarService } from '../../../../services/toast-bar.service';

import { CommonModule } from '@angular/common';
import { ButtonModule } from '@openng/optimus-ui/button';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { GreyOverlayService } from '../../../../services/grey-overlay.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-recipient',
    imports: [
        ReactiveFormsModule,
        TableModule,
        FormsModule,
        CommonModule,
        ButtonModule,
        InputTextModule,
    ],
    templateUrl: './recipient.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './recipient.component.scss'
})
export class RecipientComponent implements OnInit, OnDestroy {
  recipient: Recipient | undefined;
  recipients: Recipient[] = [];
  clonedRecipients: { [s: string]: Recipient } = {};

  recipientForm = new FormGroup({
    customerId: new FormControl('', Validators.required),
    recipientName: new FormControl('', Validators.required),
  });

  private _destroy$ = new Subject<void>();

  constructor(
    private responsysService: ResponsysService,
    private toastBarService: ToastBarService,
    private greyOverlayService: GreyOverlayService
  ) {
  }

  ngOnInit(): void {
    this.responsysService
      .getAllRecipients()
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (recipients) => {
          this.recipients = recipients;
        },
        error: (err) => {
          console.error('Error while getting recipients!', err);
        },
      });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  addRecipient() {
    this.recipient = {
      customerId: this.recipientForm.value.customerId!,
      recipientName: this.recipientForm.value.recipientName!,
    };

    this.greyOverlayService.enableGreyOverlay();
    this.responsysService
      .addRecipient(this.recipient)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (recipient: any) => {
          this.recipientForm.reset();
          this.toastBarService.showToast({
            severity: 'success',
            summary: 'Success',
            detail: `Recipient succesfully added!`,
          });
          this.recipients.push(recipient);
        },
        error: (err) => {
          console.error('Error while adding the recipient', err);
          this.toastBarService.showToast({
            severity: 'error',
            summary: 'Error',
            detail: `Error while adding the recipient!`,
          });
        },
        complete: () => {
          this.greyOverlayService.disableGreyOverlay();
        },
      });
  }

  deleteRecipient(recipient: Recipient) {
    this.greyOverlayService.enableGreyOverlay();
    this.responsysService
      .deleteRecipient(recipient.customerId)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          this.toastBarService.showToast({
            severity: 'success',
            summary: 'Success',
            detail: `Recipient succesfully deleted!`,
          });

          // Delete recipient from table
          const deleteIndex = this.recipients.findIndex(
            (r) => r.customerId === recipient.customerId
          );
          this.recipients.splice(deleteIndex, 1);
        },
        error: (err) => {
          console.error('Error while deleted the recipient', err);
          this.toastBarService.showToast({
            severity: 'error',
            summary: 'Error',
            detail: `Error while deleted the recipient!`,
          });
        },
        complete: () => {
          this.greyOverlayService.disableGreyOverlay();
        },
      });
  }

  editRecipientSave(recipient: any) {
    this.greyOverlayService.enableGreyOverlay();
    this.responsysService
      .editRecipient(recipient)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          this.toastBarService.showToast({
            severity: 'success',
            summary: 'Success',
            detail: `Recipient succesfully edited!`,
          });
        },
        error: (err) => {
          console.error('Error while editing the recipient', err);
          this.toastBarService.showToast({
            severity: 'error',
            summary: 'Error',
            detail: `Error while editing the recipient!`,
          });
        },
        complete: () => {
          this.greyOverlayService.disableGreyOverlay();
        },
      });
  }

  editRecipientCancel(recipient: any, index: any) {
    this.recipients[index] = this.clonedRecipients[recipient.id as string];
    delete this.clonedRecipients[recipient.id as string];
  }

  editRecipientInit(recipient: any) {
    this.clonedRecipients[recipient.id as string] = {...recipient};
  }
}
